/**
 * POSScreen — головна сторінка POS-режиму.
 *
 * Flow:
 *  1. Якщо нема workstation_id у LocalStorage → POSWorkstationPicker (модал).
 *  2. Завантажуємо workstation з backend для розкриття orgs/price_group/etc.
 *  3. Якщо нема відкритої зміни → POSShiftOpenDialog.
 *  4. Інакше — split layout: POSReceiptCart + POSCatalog + bottom payment bar.
 *  5. Клік платежу → POSPaymentDialog → success → cleanCart.
 */
import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { retailPosWorkstations } from '@/api/retailPosWorkstations';
import { retailLoyaltySettings } from '@/api/retailLoyaltySettings';
import { useWorkstation } from '@/hooks/pos/useWorkstation';
import { useCurrentShift } from '@/hooks/pos/useCurrentShift';
import { useReceiptCart } from '@/hooks/pos/useReceiptCart';
import type { CustomerCardLookup } from '@/api/retailCustomerCards';
import type { RetailPaymentMethod } from '@/api/retailSales';
import POSLayout from './POSLayout';
import POSReceiptCart from './POSReceiptCart';
import POSCatalog from './POSCatalog';
import POSPaymentDialog from './POSPaymentDialog';
import POSShiftOpenDialog from './POSShiftOpenDialog';
import POSShiftCloseDialog from './POSShiftCloseDialog';
import POSWorkstationPicker from './POSWorkstationPicker';
import './pos.css';

export default function POSScreen() {
  const { workstationId, setWorkstationId } = useWorkstation();
  const [showWorkstationPicker, setShowWorkstationPicker] = useState(false);
  const [showCloseShift, setShowCloseShift] = useState(false);
  const [showOpenShift, setShowOpenShift] = useState(false);
  const [paymentMethod, setPaymentMethod] = useState<RetailPaymentMethod | null>(null);
  const [customerCard, setCustomerCard] = useState<CustomerCardLookup | null>(null);
  const [mobileCatalogOpen, setMobileCatalogOpen] = useState(false);

  const cart = useReceiptCart();
  const { shift, isLoading: shiftLoading, refetch: refetchShift } = useCurrentShift(workstationId);

  // Завантажуємо workstation з backend (для name + organization_id + price_group_id).
  const wsQuery = useQuery({
    queryKey: ['retail-pos-workstation', workstationId],
    queryFn: () => retailPosWorkstations.get(workstationId!),
    enabled: !!workstationId,
  });
  const workstation = wsQuery.data ?? null;

  const loyaltyQuery = useQuery({
    queryKey: ['pos-loyalty-settings', workstation?.organization_id],
    queryFn: () =>
      retailLoyaltySettings.list({
        organization_id: workstation!.organization_id,
        is_active: true,
        limit: 1,
      }),
    enabled: !!workstation,
  });
  const loyaltySettings = loyaltyQuery.data?.items[0] ?? null;

  // Якщо немає workstation у LocalStorage — показуємо picker.
  useEffect(() => {
    if (!workstationId) setShowWorkstationPicker(true);
  }, [workstationId]);

  // Авто-показ діалогу «Відкриття зміни» при першому вході (коли workstation
  // обрано, зміни нема, і касир ще не закривав цей діалог явно).
  // Скасування у діалозі → касир залишається у idle-mode з банером.
  useEffect(() => {
    if (workstation && !shift && !shiftLoading && !showCloseShift) {
      setShowOpenShift((prev) => prev || !sessionStorage.getItem('pos.open-dismissed'));
    }
    if (shift) {
      // Зміна відкрита — скидаємо «dismissed» прапор для наступного циклу.
      sessionStorage.removeItem('pos.open-dismissed');
      setShowOpenShift(false);
    }
  }, [workstation, shift, shiftLoading, showCloseShift]);

  if (showWorkstationPicker) {
    return (
      <POSWorkstationPicker
        onPick={(id) => {
          setWorkstationId(id);
          setShowWorkstationPicker(false);
        }}
        onClose={workstationId ? () => setShowWorkstationPicker(false) : undefined}
      />
    );
  }

  if (wsQuery.isLoading || shiftLoading) {
    return (
      <POSLayout
        workstation={null}
        shift={null}
        onCloseShift={() => undefined}
        onChangeWorkstation={() => setShowWorkstationPicker(true)}
      >
        <div style={{ padding: '2rem', textAlign: 'center' }}>Завантаження...</div>
      </POSLayout>
    );
  }

  if (!workstation) {
    return (
      <POSLayout
        workstation={null}
        shift={null}
        onCloseShift={() => undefined}
        onChangeWorkstation={() => setShowWorkstationPicker(true)}
      >
        <div style={{ padding: '2rem', textAlign: 'center', color: 'var(--danger)' }}>
          Робоче місце не знайдено.{' '}
          <button onClick={() => setShowWorkstationPicker(true)}>Обрати інше</button>
        </div>
      </POSLayout>
    );
  }

  return (
    <POSLayout
      workstation={workstation}
      shift={shift}
      onOpenShift={() => setShowOpenShift(true)}
      onCloseShift={() => setShowCloseShift(true)}
      onChangeWorkstation={() => setShowWorkstationPicker(true)}
    >
      {!shift ? (
        <div className="pos-idle">
          <div style={{ fontSize: '3rem' }}>📋</div>
          <h2>Зміна не відкрита</h2>
          <p>Щоб почати продавати — відкрийте зміну.</p>
          <button
            className="pos-modal__btn-primary"
            onClick={() => {
              sessionStorage.removeItem('pos.open-dismissed');
              setShowOpenShift(true);
            }}
          >
            ▶ Відкрити зміну
          </button>
        </div>
      ) : (
        <>
          <div className="pos-split">
            <POSReceiptCart
              cart={cart}
              organizationId={workstation.organization_id}
              customerCard={customerCard}
              setCustomerCard={setCustomerCard}
              loyaltySettings={loyaltySettings}
            />
            <POSCatalog
              organizationId={workstation.organization_id}
              priceGroupId={workstation.default_price_group_id}
              isOpen={mobileCatalogOpen || !isMobileViewport()}
              onClose={() => setMobileCatalogOpen(false)}
              onAddItem={(nom, price) => {
                cart.addItem({
                  nomenclature_id: nom.id,
                  name: nom.name,
                  item_kind: nom.is_service ? 'service' : 'goods',
                  price_per_unit: price,
                  // ч.1340: остаточну ставку визначає сервер (організація, картка товару).
                  vat_rate: 20,
                });
                setMobileCatalogOpen(false);
              }}
            />
          </div>

          <div className="pos-bottom-bar">
            {isMobileViewport() && (
              <button
                className="pos-bottom-bar__add-item"
                onClick={() => setMobileCatalogOpen(true)}
                aria-label="Додати товар"
              >
                +
              </button>
            )}
            <button
              className="pos-bottom-bar__btn pos-bottom-bar__btn--cash"
              disabled={cart.isEmpty}
              onClick={() => setPaymentMethod('cash')}
            >
              💵 Готівка
            </button>
            <button
              className="pos-bottom-bar__btn pos-bottom-bar__btn--card"
              disabled={cart.isEmpty}
              onClick={() => setPaymentMethod('card')}
            >
              💳 Картка
            </button>
            <button
              className="pos-bottom-bar__btn pos-bottom-bar__btn--mixed"
              disabled={cart.isEmpty}
              onClick={() => setPaymentMethod('mixed')}
            >
              🎁 Змішана
            </button>
          </div>

          {paymentMethod && (
            <POSPaymentDialog
              cart={cart}
              workstationId={workstation.id}
              organizationId={workstation.organization_id}
              paymentMethod={paymentMethod}
              customerCard={customerCard}
              onSuccess={() => {
                cart.clear();
                setCustomerCard(null);
                setPaymentMethod(null);
              }}
              onClose={() => setPaymentMethod(null)}
            />
          )}
        </>
      )}

      {/* Open shift dialog — поверх будь-якого стану. Закривається у
          idle-mode або у активну зміну (refetchShift детектне). */}
      {showOpenShift && !shift && (
        <POSShiftOpenDialog
          workstation={workstation}
          onOpened={() => {
            setShowOpenShift(false);
            sessionStorage.removeItem('pos.open-dismissed');
            refetchShift();
          }}
          onClose={() => {
            // «Скасувати» / «✕» — НЕ повертає назад у picker. Просто закриває
            // діалог; касир залишається у idle-mode. Прапор у sessionStorage
            // блокує авто-показ до явного «Відкрити зміну».
            sessionStorage.setItem('pos.open-dismissed', '1');
            setShowOpenShift(false);
          }}
        />
      )}

      {/* Close shift dialog — самостійно керує success-станом + Z-звіт. */}
      {showCloseShift && shift && (
        <POSShiftCloseDialog
          shift={shift}
          onClosed={() => {
            // Касир натиснув «Готово» у success-екрані → закриваємо +
            // refetchShift → POSScreen перейде у idle (shift=null).
            setShowCloseShift(false);
            refetchShift();
          }}
          onClose={() => setShowCloseShift(false)}
        />
      )}
    </POSLayout>
  );
}

function isMobileViewport(): boolean {
  if (typeof window === 'undefined') return false;
  return window.innerWidth < 768;
}
